<template>
  <v-dialog
    v-model="dialog"
    class="select-student-dialog"
    max-width="900px"
    :fullscreen="isMobile">
    <!--activator button-->
    <v-btn
      class="main-action-button active"
      slot="activator"
      dark
      block>
      <v-icon class="pr-3">create</v-icon>
      Create Service Log
    </v-btn>
    <!--main content-->
    <v-card v-if="!isMobile" style="overflow: hidden;">
      <v-toolbar color="primary" dark>
        <v-toolbar-title v-if="isAdmin && !showSelectStudents">
          Select Provider
        </v-toolbar-title>
        <v-toolbar-title v-else>
            <span v-if="isMobile">
              Select Service
            </span>
          <span v-else>
              Select Student Service
            </span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <!--all services switch-->
        <div v-if="showSelectStudents" style="padding-top: 20px;">
          <v-tooltip :disabled="!showTooltip" top>
            <v-switch
              v-model="allSupportsToggle"
              slot="activator">
              <template slot="label">
                  <span :class="{'accent--text font-weight-bold': allSupportsToggle, 'small': isMobile}">
                    All Services
                  </span>
              </template>
            </v-switch>
            <span>View Current Services or All Services</span>
          </v-tooltip>
        </div>
        <span v-if="isMobile">
          <v-icon @click="dialog = false">close</v-icon>
        </span>
      </v-toolbar>
      <v-card>
        <v-container grid-list-md text-xs-left>
          <!--select provider options-->
          <!--only available to proxies who can file SL for other providers-->
          <template v-if="isAdmin && !showSelectStudents">
            <v-card-text>
              <v-layout row justify-center align-center>
                <v-flex xs12 sm6>
                  <v-autocomplete
                    dense
                    label="Provider Name/Credentials"
                    placeholder="Enter Provider"
                    :items="providersArray"
                    v-model="selectedProvider"
                    :clearable="true"
                    :error-messages="providerSearchError"
                    :return-object="true"
                    :search-input.sync="providerSearchInput"
                    item-text="userId"
                    :loading="providerSearchLoading"
                    :no-filter="true"
                    @focus="clearProvidersAutoComplete">
                    <!--this is how the data in the select appears-->
                    <template slot="item" slot-scope="{item, tile}">
                      {{item.firstName}} {{item.lastName}}
                      <!--add credentials if they exist-->
                      <span v-if="item.credentialName">/{{item.credentialName}}</span>
                    </template>
                    <template slot="selection" slot-scope="{item, selected}">
                      {{item.firstName}} {{item.lastName}}
                      <!--add credentials if they exist-->
                      <span v-if="item.credentialName">/{{item.credentialName}}</span>
                    </template>
                    <!--message that displays when search matches nothing-->
                    <template slot="no-data">
                      <v-list>
                        <!--message when no input is entered-->
                        <v-list-tile v-if="!providerSearchInput">
                          Please type a Provider.
                        </v-list-tile>
                        <!--message when no provider matches text-->
                        <v-list-tile
                          v-if="providerSearchInput && !providerSearchLoading">
                          No Results
                        </v-list-tile>
                      </v-list>
                    </template>
                  </v-autocomplete>
                </v-flex>
              </v-layout>
              <v-layout justify-end>
                <v-btn
                  color="secondary"
                  @click="goToSelectStudents"
                  :disabled="!selectedProvider">
                  Select Student
                  <span class="pl-2">
                    <v-icon>mdi-arrow-right</v-icon>
                  </span>
                </v-btn>
              </v-layout>
            </v-card-text>
          </template>
          <!--SELECT STUDENT INFORMATION, NEED TO HAVE A SELECTED PROVIDER-->
          <template
            class="select-student-template"
            v-if="!isAdmin || (showSelectStudents && isAdmin)">
            <div class="student-info">
              <!--search and filter header-->
              <v-card-title style="position: relative;" class="py-0">
                <v-layout row wrap>
                  <!--find student-->
                  <v-flex xs12 sm4>
                    <v-text-field
                      label="Find Student"
                      placeholder="Enter Student"
                      :clearable="true"
                      v-model="findStudentSearch">
                    </v-text-field>
                  </v-flex>
                  <!--filter school-->
                  <v-flex xs12 sm4>
                    <v-text-field
                      label="Filter School"
                      placeholder="Enter School"
                      :clearable="true"
                      v-model="findSchoolSearch">
                    </v-text-field>
                  </v-flex>
                  <!--sort and select all button-->
                  <v-flex xs12 sm4>
                    <v-layout row wrap>
                      <v-flex xs6>
                        <v-menu open-on-hover offset-y style="width: 100%;">
                          <v-btn color="secondary" slot="activator" block>
                            <small>Sort</small>
                          </v-btn>
                          <v-list>
                            <v-list-tile>
                              <v-list-tile-title @click="sort = 'lastNameAsc'">
                                Last Name Asc
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile>
                              <v-list-tile-title @click="sort = 'lastNameDesc'">
                                Last Name Desc
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile>
                              <v-list-tile-title @click="sort = 'schoolAsc'">
                                School Name Asc
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile>
                              <v-list-tile-title @click="sort = 'schoolDesc'">
                                School Name Desc
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile>
                              <v-list-tile-title @click="sort = 'latestService'">
                                Last Service
                              </v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>
                      <v-flex xs6>
                        <v-btn color="secondary" @click="checkAllBoxes = !checkAllBoxes" block>
                          <small v-if="!checkAllBoxes">Select All</small>
                          <small v-else>Deselect All</small>
                        </v-btn>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-card-title>
              <!--STUDENT SERVICES CONTENT-->
              <v-container text-xs-left class="pa-0">
                <v-alert :value="!filteredStudents.length" type="error">
                  No Students associated with this provider.
                </v-alert>
                <v-data-table
                  class="student-container"
                  v-if="filteredStudents.length"
                  :items="filteredStudents"
                  hide-actions
                  hide-headers>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-checkbox
                        color="secondary"
                        hide-details
                        v-model="props.item.selected"
                      ></v-checkbox>
                    </td>
                    <!--STUDENT SERVICE INFORMATION-->
                    <td :class="{'warning--text': !props.item.active}">
                      <strong>{{ props.item.studentName }}</strong><br>
                      {{ props.item.studentExtId }}
                    </td>
                    <td :class="{'warning--text': !props.item.active}">
                      {{ props.item.schoolName }} ({{ props.item.grade }})<br>
                      {{ props.item.serviceName }}
                    </td>
                    <td :class="{'warning--text': !props.item.active}">
                      {{ props.item.startDate | formatDate }} - {{ props.item.endDate | formatDate }}<br>
                      {{ props.item.frequency }} <span v-if="props.item.esy">(ESY)</span>
                    </td>
                  </template>
                </v-data-table>
              </v-container>
            </div>
            <div>
              <v-layout mx-0 mt-3 style="width: 100%;">
                <v-flex xs12 class="block">
                  <v-layout row wrap>
                    <v-flex xs6>
                      <v-btn
                        class="my-0"
                        color="secondary"
                        v-if="isAdmin"
                        @click="returnToProviders">
                        <span>
                          <span class="pr-2">
                            <v-icon>mdi-arrow-left</v-icon>
                          </span>
                          Change Provider
                        </span>
                      </v-btn>
                    </v-flex>
                    <v-flex xs6 text-xs-right>
                      <v-btn
                        class="my-0"
                        color="secondary"
                        @click="onNextClicked"
                        :disabled="!filteredStudents.length">
                        <span>
                          Create Service Log
                        </span>
                        <span class="pl-2">
                          <v-icon>mdi-arrow-right</v-icon>
                        </span>
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </div>
            <v-alert :value="errMsg"
                     type="error">
              {{errMsg}}
            </v-alert>
          </template>
        </v-container>
      </v-card>
    </v-card>
    <!--mobile styling-->
    <v-card v-if="isMobile" style="overflow: hidden">
      <!--toolbar 56px-->
      <v-toolbar color="primary" dark>
        <v-toolbar-title>
          <span v-if="isAdmin && !showSelectStudents">
            Select Provider
          </span>
          <span v-else>
            Select Student
            <span v-if="!isMobile">
              Service
            </span>
          </span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <!--all services switch-->
        <div v-if="showSelectStudents" style="padding-top: 20px;">
          <v-tooltip :disabled="!showTooltip" top>
            <v-switch
              v-model="allSupportsToggle"
              slot="activator">
              <template slot="label">
                  <span :class="{'accent--text font-weight-bold': allSupportsToggle, 'small': isMobile}">
                    All Services
                  </span>
              </template>
            </v-switch>
            <span>View Current Services or All Services</span>
          </v-tooltip>
        </div>
        <span v-if="isMobile" class="ml-2">
          <v-icon @click="dialog = false">close</v-icon>
        </span>
      </v-toolbar>
      <!--select provider options-->
      <!--only available to proxies who can file SL for other providers-->
      <template v-if="isAdmin && !showSelectStudents">
        <v-card-text>
          <v-layout row justify-center align-center>
            <v-flex xs12 sm6>
              <v-autocomplete
                dense
                label="Provider Name/Credentials"
                placeholder="Enter Provider"
                :items="providersArray"
                v-model="selectedProvider"
                :clearable="true"
                :error-messages="providerSearchError"
                :return-object="true"
                :search-input.sync="providerSearchInput"
                item-text="userId"
                :loading="providerSearchLoading"
                :no-filter="true"
                @focus="clearProvidersAutoComplete">
                <!--this is how the data in the select appears-->
                <template slot="item" slot-scope="{item, tile}">
                  {{item.firstName}} {{item.lastName}}
                  <!--add credentials if they exist-->
                  <span v-if="item.credentialName">/{{item.credentialName}}</span>
                </template>
                <template slot="selection" slot-scope="{item, selected}">
                  {{item.firstName}} {{item.lastName}}
                  <!--add credentials if they exist-->
                  <span v-if="item.credentialName">/{{item.credentialName}}</span>
                </template>
                <!--message that displays when search matches nothing-->
                <template slot="no-data">
                  <v-list>
                    <!--message when no input is entered-->
                    <v-list-tile v-if="!providerSearchInput">
                      Please type a Provider.
                    </v-list-tile>
                    <!--message when no provider matches text-->
                    <v-list-tile
                      v-if="providerSearchInput && !providerSearchLoading">
                      No Results
                    </v-list-tile>
                  </v-list>
                </template>
              </v-autocomplete>
            </v-flex>
          </v-layout>
          <v-layout justify-end>
            <v-btn
              color="secondary"
              @click="goToSelectStudents"
              :disabled="!selectedProvider">
              Select Student
              <span class="pl-2">
                <v-icon>mdi-arrow-right</v-icon>
              </span>
            </v-btn>
          </v-layout>
        </v-card-text>
      </template>
      <!--select student services-->
      <span v-if="!isAdmin || (showSelectStudents && isAdmin)">
          <div>
            <v-container>
              <v-layout row wrap>
                <!--find student-->
                <v-flex :class="{'pa-0': isMobile}" xs12 sm4>
                  <v-text-field
                    label="Find Student"
                    placeholder="Enter Student"
                    :clearable="true"
                    v-model="findStudentSearch">
                  </v-text-field>
                </v-flex>
                <!--filter school-->
                <v-flex :class="{'pa-0': isMobile}" xs12 sm4>
                  <v-text-field
                    label="Filter School"
                    placeholder="Enter School"
                    :clearable="true"
                    v-model="findSchoolSearch">
                  </v-text-field>
                </v-flex>
                <!--sort and select all button-->
                <v-flex :class="{'pa-0': isMobile}" xs12 sm4>
                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-menu offset-y style="width: 100%;">
                        <v-btn color="secondary" slot="activator" block>
                          <span>Sort</span>
                        </v-btn>
                        <v-list>
                          <v-list-tile>
                            <v-list-tile-title @click="sort = 'lastNameAsc'">
                              Last Name Asc
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile>
                            <v-list-tile-title @click="sort = 'lastNameDesc'">
                              Last Name Desc
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile>
                            <v-list-tile-title @click="sort = 'schoolAsc'">
                              School Name Asc
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile>
                            <v-list-tile-title @click="sort = 'schoolDesc'">
                              School Name Desc
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile>
                            <v-list-tile-title @click="sort = 'latestService'">
                              Last Service
                            </v-list-tile-title>
                          </v-list-tile>
                        </v-list>
                      </v-menu>
                    </v-flex>
                    <v-flex xs12>
                      <v-btn color="secondary" @click="checkAllBoxes = !checkAllBoxes" block>
                        <span v-if="!checkAllBoxes">Select All</span>
                        <span v-else>Deselect All</span>
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-container>
          </div>
          <div class="select-student-content pb-5">
            <v-container text-xs-left pa-0>
              <v-alert :value="!filteredStudents.length" type="error">
                No Students associated with this provider.
              </v-alert>
              <v-data-table
                v-if="filteredStudents.length"
                :items="filteredStudents"
                hide-actions
                hide-headers>
                <template slot="items" slot-scope="props">
                  <td :class="{'pa-2': isMobile}">
                    <v-checkbox
                      color="secondary"
                      hide-details
                      v-model="props.item.selected"
                    ></v-checkbox>
                  </td>
                  <!--MOBILE STUDENT SERVICE INFORMATION-->
                  <td class="py-1 px-0" v-if="isMobile">
                    <strong>
                      <span>
                        {{ props.item.studentName }}
                        ({{ props.item.studentExtId }})
                      </span>
                    </strong>
                    <br>
                    <span class="pr-1">
                      {{ props.item.schoolName }}
                    </span>
                    {{ props.item.grade }}
                    <br>
                    {{ props.item.startDate | formatDate }} - {{ props.item.endDate | formatDate }}<br>
                    {{ props.item.frequency }} <span v-if="props.item.esy">(ESY)</span>
                  </td>
                  <!--NON MOBILE STUDENT SERVICE INFORMATION-->
                  <td
                    v-if="!isMobile"
                    :class="{'warning--text': !props.item.active}">
                    <strong>{{ props.item.studentName }}</strong><br>
                    {{ props.item.studentExtId }}
                  </td>
                  <td
                    v-if="!isMobile"
                    :class="{'warning--text': !props.item.active}">
                    {{ props.item.schoolName }}<br>
                    {{ props.item.grade }}
                  </td>
                  <td
                    v-if="!isMobile"
                    :class="{'warning--text': !props.item.active}">
                    {{ props.item.startDate | formatDate }} - {{ props.item.endDate | formatDate }}<br>
                    {{ props.item.frequency }} <span v-if="props.item.esy">(ESY)</span>
                  </td>
                </template>
              </v-data-table>
            </v-container>
          </div>
          <div class="footer">
          <v-layout style="width: 100%; margin: 0;">
            <v-flex xs12 class="block">
              <v-layout row wrap>
                <v-flex xs6>
                  <v-btn
                    class="my-0"
                    color="secondary"
                    :block="isMobile"
                    v-if="isAdmin"
                    @click="returnToProviders">
                    <span :class="{'small pt-1': isMobile}">
                    <span class="pr-2">
                      <v-icon :small="isMobile">mdi-arrow-left</v-icon>
                    </span>
                    Change Provider
                    </span>
                  </v-btn>
                </v-flex>
                <v-flex xs6 text-xs-right>
                  <v-btn
                    class="my-0"
                    color="secondary"
                    @click="onNextClicked"
                    :block="isMobile"
                    :disabled="!filteredStudents.length">
                    <span :class="{'small pt-1': isMobile}">
                      Create Service Log
                    </span>
                    <span class="pl-2">
                      <v-icon :small="isMobile">mdi-arrow-right</v-icon>
                    </span>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </div>
        </span>
    </v-card>
    <full-screen-loading-spinner v-if="showLoading"></full-screen-loading-spinner>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import * as moment from 'moment';
import api from '@/utils/api';
import JWTHelper from '@/jwt-token';
import FullScreenLoadingSpinner from '@/components/full-screen-loading-spinner';
import * as Constants from '@/constants';

export default {
  name: 'SelectStudent',
  components: {
    FullScreenLoadingSpinner
  },
  computed: {
    ...mapGetters({
      showTooltip: 'showTooltip'
    }),
    filteredStudents() {
      // make a copy of the students array
      let students = this.studentsArray.slice();
      // if allSupportsToggle is false (not toggled),
      // only show end dates that are current
      if (!this.allSupportsToggle) {
        students = students.filter((student) => {
          return (student.active || student.exitCode === '055') && moment(student.endDate, 'YYYY-MM-DD') > moment();
        });
      }

      // if text is entered in the find student input,
      // filter the list of students based on the input
      if (this.findStudentSearch) {
        students = students.filter((student) => {
          // if search is not a number, search start of first or last name
          if (isNaN(this.findStudentSearch)) {
            const regex = new RegExp(`^${this.findStudentSearch}`, 'i');
            const [lastName, firstName] = student.studentName.split(',');
            return regex.test(firstName.trim()) || regex.test(lastName.trim());
          }
          const regex = new RegExp(`^${this.findStudentSearch}`);
          return regex.test(student.studentExtId);
        });
      }

      // if location search, filter based on school
      if (this.findSchoolSearch) {
        students = students.filter((student) => {
          const regex = new RegExp(`^${this.findSchoolSearch}`, 'i');
          return regex.test(student.schoolName);
        });
      }

      if (this.checkAllBoxes) {
        students = students.map((student) => {
          return {
            ...student,
            selected: this.checkAllBoxes
          };
        });
      }

      return students;
    },
    isMobile() {
      return this.$mq === 'mobile';
    }
  },
  data() {
    return {
      dialog: false,
      allSupportsToggle: false,
      checkAllBoxes: false,
      nameSortAsc: false,
      findStudentSearch: '',
      locationSearch: null,
      errMsg: '',
      sort: '',
      selectAll: false,
      selectedProvider: null,
      showSelectStudents: false,
      sheet: false,
      locationsArray: [],
      timeout: null,
      searchInput: null,
      locationsLoading: false,
      // provider autocomplete
      providerSearchError: '',
      providerSearchInput: null,
      providerSearchLoading: false,
      providersArray: [],
      // find student autocomplete
      studentSearchError: '',
      studentSearchInput: null,
      studentSearchLoading: false,
      studentsArray: [],
      isAdmin: null,
      providerId: null,
      showLoading: false,
      findSchoolSearch: ''
    };
  },
  created() {
    this.isAdmin = JWTHelper.isAdmin();
    // set providerId if it exists in local storage
    this.providerId = localStorage.getItem('provider-id') || null;
    // if they are not an admin, they will only have the option of selecting students
    // get students for non admin users
    if (!this.isAdmin) {
      this.getStudentList('lastNameAsc');
    }
    // set selected provider to logged in user
    this.timeout = setTimeout(() => {
      api.get(Constants.OTSL_GET_ACTIVE_PROVIDER_LIST_URL(JWTHelper.user().fullName))
        .then((res) => {
          this.selectedProvider = res.data.data[0];
          this.providersArray = res.data.data;
        })
        .catch((error) => {
          this.providerSearchError = error.message;
        })
        .finally(() => {
          // stop loading
          this.providerSearchLoading = false;
        });
    }, 300);
  },
  methods: {
    clearProvidersAutoComplete() {
      if (!this.selectedProvider) {
        this.providersArray = [];
      }
    },
    onNextClicked() {
      const selectedStudents = this.filteredStudents.filter((student) => {
        return student.selected;
      });
      if (selectedStudents.length) {
        let idString = '';
        for (let i = 0, l = selectedStudents.length; i < l; i++) {
          idString += selectedStudents[i].serviceId;
          // if the counter is less than the array length
          // and the array length is longer than one
          // add a comma
          if (i < l - 1 && l > 1) {
            idString += ',';
          }
        }
        localStorage.setItem('selected-students', idString);
        this.$router.push('/create');
      } else {
        this.errMsg = 'Please select students.';
      }
    },
    getStudentList(searchParam) {
      this.showLoading = true;
      api.get(Constants.OTSL_PROVIDERS_STUDENT_SERVICES_LIST_URL(this.providerId, searchParam))
        .then((res) => {
          // flatten the nested arrays
          this.studentsArray = res.data.data;
          this.showSelectStudents = true;
          this.showLoading = false;
        });
    },
    goToSelectStudents() {
      // set the providerId on localStorage
      if (this.selectedProvider) {
        // set the proivderId locally to use in filtering schools
        this.providerId = this.selectedProvider.userId;
        // assign providerId to localstorage
        localStorage.setItem('provider-id', this.selectedProvider.userId.toString());
        this.getStudentList('lastNameAsc');
        // clear the selected provider
        this.selectedProvider = '';
      }
    },
    clearParameters() {
      this.selectedProvider = '';
      this.findStudentSearch = '';
      this.findSchoolSearch = '';
      this.allSupportsToggle = false;
      this.errMsg = '';
    },
    returnToProviders() {
      this.clearParameters();
      this.showSelectStudents = false;
    }
  },
  watch: {
    dialog() {
      // dialog will be true when opening the dialog, clear out values
      if (this.dialog) {
        this.errorMsg = null;
        this.showSelectStudents = false;
        this.sheet = true;
        // reorder the student list by lastNameAsc
        if (!this.isAdmin) this.getStudentList('lastNameAsc');
      } else {
        this.clearParameters();
      }
    },
    providerSearchInput() {
      // show loading
      this.providerSearchLoading = true;
      // clear timeout if function is called before setTimeout fires
      clearTimeout(this.timeout);
      // make sure the user types something before firing request
      if (this.providerSearchInput) {
        // fire the request after a delay
        this.timeout = setTimeout(() => {
          api.get(Constants.OTSL_GET_ACTIVE_PROVIDER_LIST_URL(this.providerSearchInput))
            .then((res) => {
              this.providersArray = res.data.data;
            })
            .catch((error) => {
              this.providerSearchError = error.message;
            })
            .finally(() => {
              // stop loading
              this.providerSearchLoading = false;
            });
        }, 300);
      } else {
        this.providerSearchLoading = false;
      }
    },
    // watcher on sort dropdown
    sort() {
      this.getStudentList(this.sort);
    },
    selectedProvider(newVal, oldVal) {
      if (!newVal && oldVal) this.providersArray = [];
    }
  }
};
</script>

<style lang="scss" scoped>
  .select-student-dialog {
    width: 100%;
  }

  .student-container {
    flex: 1 0 auto;
    max-height: 400px;
    overflow: auto;
  }

  .select-student-template {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .student-info {
    flex: 1 0 auto;
  }

  .footer {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 36px;
    background-color: var(--v-primary-base);
  }

  .header {
    background-color: #d3d3d3;
  }

  .select-student-content {
    /*subtract 56px for toolbar and 184px for the header content and 60px for the footer*/
    height: calc(100vh - 300px);
    overflow: auto;
  }
</style>



// WEBPACK FOOTER //
// src/components/select-student-dialog.vue